import { defineMessages } from 'react-intl';

import type { Dict } from '@sorare/wallet-shared';

export const messages = defineMessages<keyof Dict>({
  address: {
    defaultMessage: 'Address',
  },
  approveMigrator: {
    defaultMessage:
      'Your approval is required to be able to trade Cards with other managers.',
  },
  availableAmountForDeposit: {
    defaultMessage: 'Available funds for deposit',
  },
  depositAvailableSoon: {
    defaultMessage:
      'Your deposit will be available in your balance in a moment.',
  },
  insufficientFundsForDeposit: {
    defaultMessage: 'Insufficient funds to cover transaction fees',
  },
  cancel: {
    defaultMessage: 'Cancel',
  },
  changePasswordTitle: {
    defaultMessage: 'Change password',
  },
  currentPassword: {
    defaultMessage: 'Current password',
  },
  currentPasswordPlaceholder: {
    defaultMessage: 'Your current password',
  },
  depositAmount: {
    defaultMessage: 'Deposit amount',
  },
  depositFundsSubmit: {
    defaultMessage: 'Deposit',
  },
  edit: {
    defaultMessage: 'Edit',
  },
  email: {
    defaultMessage: 'Email',
  },
  emailUnconfirmed: {
    defaultMessage:
      'not confirmed, we have sent you a confirmation link by email',
  },
  emailUnconfirmedTitle: {
    defaultMessage: "You've not confirmed your account.",
  },
  emailUnconfirmedBody: {
    defaultMessage:
      "We've re-sent the confirmation email. Please check your email to confirm your account.",
  },
  forgotPassword: {
    defaultMessage: 'Forgot password?',
  },
  forgotPasswordTitle: {
    defaultMessage: 'Forgotten password',
  },
  gasPrice: {
    defaultMessage: 'Gas Price (Gwei)',
  },
  gasPricePlaceholder: {
    defaultMessage: '0',
  },
  invalidCredentials: {
    defaultMessage: 'invalid credentials',
  },
  loading: {
    defaultMessage: 'Loading',
  },
  nickname: {
    defaultMessage: 'Username',
  },
  noFundsAvailable: {
    defaultMessage: 'No funds to deposit',
  },
  noWallet: {
    defaultMessage: "You don't have a wallet",
  },
  password: {
    defaultMessage: 'Password',
  },
  recoverWallet: {
    defaultMessage: 'Your wallet must be recovered',
  },
  recoveryKey: {
    defaultMessage: 'Recovery key',
  },
  recoveryKeyIsInvalid: {
    defaultMessage: 'This recovery key is invalid',
  },
  recoverKeyTitle: {
    defaultMessage: 'Recover wallet',
  },
  resetPasswordTitle: {
    defaultMessage: 'Reset password',
  },
  signIn: {
    defaultMessage: 'Sign in',
  },
  signInTitle: {
    defaultMessage: 'Sign in to Sorare',
  },
  signMigration: {
    defaultMessage: 'Your signature is required to trade with other managers.',
  },
  signUp: {
    defaultMessage: 'Sign up',
  },
  signUpEmail: {
    defaultMessage: 'Sign up with Email',
  },
  signUpApple: {
    defaultMessage: 'Sign up with Apple',
  },
  signUpGoogle: {
    defaultMessage: 'Sign up with Google',
  },
  oAuthSeparator: {
    defaultMessage: 'or',
  },
  goBack: {
    defaultMessage: 'Go back',
  },
  signUpAgreePartnersOffers: {
    defaultMessage:
      "I want to receive email offers from Sorare's partnering clubs.",
  },
  signUpTerms: {
    defaultMessage:
      "I agree to Sorare's <t>Terms of Service</t> and <g>Game Rules</g>",
  },
  signUpTitle: {
    defaultMessage: 'Sign up to Sorare',
  },
  signUpSubtitle: {
    defaultMessage: 'Create your Sorare account',
  },
  submit: {
    defaultMessage: 'Submit',
  },
  transactionCost: {
    defaultMessage: 'Transaction cost',
  },
  twoFaCode: {
    defaultMessage: '2FA Code',
  },
  unconfirmedDevice: {
    defaultMessage: 'Confirm your device to access your wallet',
  },
  unlock: {
    defaultMessage: 'Unlock',
  },
  unlockWalletPlaceholder: {
    defaultMessage: 'Enter your password',
  },
  walletLocked: {
    defaultMessage: 'Wallet locked',
  },
  walletUnlocked: {
    defaultMessage: 'Wallet unlocked',
  },
  close: {
    defaultMessage: 'Close',
  },
  privateKey: {
    defaultMessage: 'Your private key',
  },
  privateKeyWarning: {
    defaultMessage:
      'Warning: Do not share your private keys, our support will never ask them. Anyone with your private key could steal all your ETH and Cards.',
  },
  pwned: {
    defaultMessage:
      'This password previously appeared in a public data breach on another website. Please choose a stronger password.',
  },
  pwnedByNow: {
    defaultMessage:
      "Your password isn't secure anymore as it appears in a public data breach from another website. Please use the {link} link to change your password first.",
  },
  pwnedByNowTitle: {
    defaultMessage: 'Password compromised',
  },
  levenshtein: {
    defaultMessage: 'too close to your email or username',
  },
  length: {
    defaultMessage: 'should be at least 6 characters long',
  },
  generateWallet: {
    defaultMessage: 'Please choose a password to secure your account.',
  },
  updateWallet: {
    defaultMessage:
      'Your wallet needs to be updated, please enter your password.',
  },
  message: {
    defaultMessage: 'Message',
  },
  signature: {
    defaultMessage: 'Signature',
  },
  ethereumPrivateKey: {
    defaultMessage: 'Ethereum private key',
  },
  ethereumPublicAddress: {
    defaultMessage: 'Ethereum public address',
  },
  ethereumKeysTitle: {
    defaultMessage: 'Ethereum',
  },
  ethereumKeysDescription: {
    defaultMessage:
      'This is your Ethereum Wallet. You can send ETH (and only ETH) to the public address.',
  },
  starkwarePrivateKey: {
    defaultMessage: 'Starkware private key',
  },
  starkwarePublicKey: {
    defaultMessage: 'Starkware public key',
  },
  starkwareKeysTitle: {
    defaultMessage: 'Starkware',
  },
  starkwareKeysDescription: {
    defaultMessage:
      'This is your Sorare (Starkware) Wallet. This cannot receive ETH directly and is only used within Sorare.',
  },
  errorTrackingTitle: {
    defaultMessage: 'Something wrong happened…',
  },
  errorTrackingBody: {
    defaultMessage: 'We are investigating.',
  },
  newPassword: {
    defaultMessage: 'New password',
  },
  confirmNewPassword: {
    defaultMessage: 'Confirm new password',
  },
  differentPasswords: {
    defaultMessage: 'Passwords are different',
  },
  newPasswordPlaceholder: {
    defaultMessage: 'Your new password',
  },
  changePasswordSubmit: {
    defaultMessage: 'Change',
  },
  tooManyLoginAttempts: {
    defaultMessage: 'Too many login attempts. Please try again later',
  },
  unableToRetrieveKeys: {
    defaultMessage: 'We could not retrieve your wallet keys. Please try again.',
  },
  trustedPartners: {
    defaultMessage: 'Trusted Partners',
  },
  trustedPartnersDesc1: {
    defaultMessage:
      'Sorare will only share information with trusted partners subject to your express consent.',
  },
  trustedPartnersDesc2: {
    defaultMessage:
      'You will find the list of our trusted partners and more information on their practice below.',
  },
  trustedPartnersDesc3: {
    defaultMessage:
      'You can consent or oppose to data sharing by checking / unchecking the boxes in front of each partner',
  },
  trustedPartnersHelper: {
    defaultMessage:
      'Once you have agreed to the sharing of your information with a specific partner, you can still withdraw your consent to the processing of your data by a specific partner at any time. In order to do so, please contact them directly by referring to their privacy policy listed above.',
  },
  passwordIsInvalid: {
    defaultMessage: 'Password is invalid',
  },
  next: {
    defaultMessage: 'Next',
  },
  orQuickAccess: {
    defaultMessage: 'Or quick access',
  },
  'passwordForgotten?': {
    defaultMessage: 'Forgot password?',
  },
  optional: {
    defaultMessage: 'Optional',
  },
  required: {
    defaultMessage: 'Required',
  },
  iAgreeToShare: {
    defaultMessage:
      "I agree to share my information with Sorare's trusted partners for marketing and promotional purposes",
  },
  iAgreeToShareV2: {
    defaultMessage:
      "I agree to share my information with Sorare's trusted partners listed <partnerslist>here</partnerslist> for marketing and promotional purposes",
  },
  privacyPolicy: {
    defaultMessage: 'Privacy Policy',
  },
  alreadyHaveAccount: {
    defaultMessage: 'Have an account?',
  },
  dontHaveAccount: {
    defaultMessage: "Don't have an account?",
  },
  iAgreeToTermsAndConditionsAndAgeLimit: {
    defaultMessage: `I certify that I am 18 years old or older, agree to Sorare's <terms>Terms and Conditions</terms> and that my personal data will be processed pursuant to the <privacy>Privacy Policy</privacy>.`,
  },
  youMustAcceptTermsAndConditions: {
    defaultMessage:
      'You must accept our Terms and Conditions and Privacy Policy',
  },

  youMustAcceptAgeLimit: {
    defaultMessage: 'You must be 18 years old or older',
  },
  navigationBack: {
    defaultMessage: 'Back',
  },
  forgotPasswordHelper: {
    defaultMessage:
      'Enter the email address associated with your Sorare account.',
  },
  forgotPasswordEmailSent: {
    defaultMessage: 'Please check your email to reset your password.',
  },
  // Partners Optin
  epl: {
    defaultMessage: 'Premier League',
  },
  eredivisie: {
    defaultMessage: 'Eredivisie',
  },
  laliga: {
    defaultMessage: 'LALIGA',
  },
  'ligue-1-fr': {
    defaultMessage: 'Ligue 1',
  },
  nba: {
    defaultMessage: 'NBA - National Basketball Association',
  },
  nbpa: {
    defaultMessage: 'NBPA - National Basketball Players Association',
  },
  mlb: {
    defaultMessage: 'MLB - Major League Baseball',
  },
  mls: {
    defaultMessage: 'MLS - Major League Soccer',
  },
  mlbpa: {
    defaultMessage: 'MLBPA - Major League Baseball Players Association',
  },
  eplPolicyUrl: {
    defaultMessage: 'https://www.premierleague.com/privacy-policy',
  },
  eredivisiePolicyUrl: {
    defaultMessage: 'https://eredivisie.eu/privacy-statement',
  },
  laligaPolicyUrl: {
    defaultMessage: 'https://www.laliga.com/en-US/legal/politica-privacidad',
  },
  'ligue-1-frPolicyUrl': {
    defaultMessage: 'https://ligue1.fr/fr/legal/data-protection',
  },
  nbaPolicyUrl: {
    defaultMessage: 'https://www.nba.com/privacy-policy',
  },
  nbpaPolicyUrl: {
    defaultMessage: 'https://nbpa.com/privacypolicy',
  },
  mlbPolicyUrl: {
    defaultMessage: 'https://www.mlb.com/official-information/privacy-policy',
  },
  mlsPolicyUrl: {
    defaultMessage: 'https://www.mlssoccer.com/legal/privacy-policy',
  },
  mlbpaPolicyUrl: {
    defaultMessage: 'http://mlb.mlb.com/pa/info/privacy.jsp',
  },
  solanaPrivateKey: {
    defaultMessage: 'Solana private key',
  },
  solanaPublicAddress: {
    defaultMessage: 'Solana public address',
  },
  solanaKeysComingSoon: {
    defaultMessage:
      'Direct deposits to this address coming soon. Do not deposit yet',
  },
  solanaKeysTitle: {
    defaultMessage: 'Solana',
  },
  solanaKeysDescription: {
    defaultMessage:
      'This is your Solana Wallet. You can send SOL (and only SOL) to the public address.',
  },
  exportSolanaPrivateKey: {
    defaultMessage: 'Export Solana private key',
  },
  solanaPrivateKeyWarning: {
    defaultMessage:
      'Warning: Do not share your private keys, our support will never ask them. Anyone with your private key could steal all your SOL and Cards.',
  },
});
